import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtValue, fmtCurrency, RAG } from "@/lib/format";
import { useLookups } from "@/hooks/useLookups";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Lightning, Warning, CheckCircle, Info, Target } from "@phosphor-icons/react";

const MISSIONS = [
  "Course Planning", "Lead Generation", "Telecalling", "Counselling & Admissions",
  "Business Development", "Marketing Coordination", "Faculty Coordination",
  "Student Experience", "Operations", "Placement Support", "Revenue Growth",
];

const insightIcon = { alert: Warning, warning: Info, positive: CheckCircle };
const insightColor = { alert: "text-rose bg-rose/10", warning: "text-gold bg-gold/10", positive: "text-emerald bg-emerald/10" };

export default function Dashboard() {
  const { lists, settings } = useLookups();
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [course, setCourse] = useState("all");
  const [member, setMember] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    if (course !== "all") params.course = course;
    if (member !== "all") params.member = member;
    const res = await api.get("/analytics/dashboard", { params });
    setData(res.data);
  }, [start, end, course, member]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate">Loading dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-lg border border-slate-200 p-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate font-semibold block mb-1">From</label>
          <Input data-testid="filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40 focus-visible:ring-gold" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate font-semibold block mb-1">To</label>
          <Input data-testid="filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40 focus-visible:ring-gold" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate font-semibold block mb-1">Course</label>
          <Select value={course} onValueChange={setCourse}>
            <SelectTrigger data-testid="filter-course" className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {(lists.courses || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate font-semibold block mb-1">Team Member</label>
          <Select value={member} onValueChange={setMember}>
            <SelectTrigger data-testid="filter-member" className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Members</SelectItem>
              {(lists.team || []).map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Scorecard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-scorecard">
        {data.kpis.map((k) => {
          const rag = RAG[k.rag];
          return (
            <div key={k.key} className="bg-white rounded-lg border border-slate-200 p-4 relative overflow-hidden">
              <div className={`absolute top-0 left-0 h-full w-1 ${rag.dot}`} />
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-slate font-semibold">{k.label}</span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${rag.bg} ${rag.text}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${rag.dot}`} />{rag.label}
                </span>
              </div>
              <div className="font-heading text-3xl font-extrabold text-navy tabular-nums">{fmtValue(k.actual, k.format)}</div>
              {k.target > 0 && <div className="text-xs text-slate mt-1">Target: {fmtValue(k.target, k.format)}</div>}
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue Trend vs Target">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${v / 100000}L`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#0F172A" strokeWidth={2.5} dot={false} name="Actual" />
              <Line type="monotone" dataKey="target" stroke="#C9A24B" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead → Admission Funnel">
          <div className="space-y-3 pt-2">
            {data.funnel.map((s, i) => {
              const max = data.funnel[0].value || 1;
              const colors = ["#0F172A", "#64748B", "#C9A24B", "#059669"];
              return (
                <div key={s.stage}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-navy font-medium">{s.stage}</span>
                    <span className="text-slate">{s.value} · {Math.round((s.value / max) * 100)}%</span>
                  </div>
                  <div className="h-4 rounded bg-slate-100 overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(s.value / max) * 100}%`, background: colors[i] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Batch Occupancy by Course">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.occupancy_by_course} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="course" width={120} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="occupancy" radius={[0, 4, 4, 0]}>
                {data.occupancy_by_course.map((e, i) => (
                  <Cell key={i} fill={e.occupancy >= 75 ? "#059669" : e.occupancy >= 50 ? "#C9A24B" : "#DC2626"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Channel-wise CPL (Cost per Lead)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.channel_cpl}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="channel" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Bar dataKey="cpl" fill="#0F172A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights + Goals/Mission */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={18} weight="fill" className="text-gold" />
            <h3 className="font-heading font-bold text-navy">Auto Insights</h3>
          </div>
          <div className="space-y-2" data-testid="insights-feed">
            {data.insights.length === 0 && <p className="text-sm text-slate">No alerts — everything on track.</p>}
            {data.insights.map((ins, i) => {
              const Icon = insightIcon[ins.type] || Info;
              return (
                <div key={i} className="flex items-start gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${insightColor[ins.type]}`}><Icon size={15} weight="fill" /></div>
                  <p className="text-sm text-navy">{ins.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-navy text-white rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Target size={18} weight="fill" className="text-gold" />
            <h3 className="font-heading font-bold">Mission & Responsibilities</h3>
          </div>
          <p className="text-slate-300 text-sm mb-4 leading-relaxed">{settings.mission}</p>
          <div className="flex flex-wrap gap-1.5">
            {MISSIONS.map((m) => (
              <span key={m} className="text-[10px] px-2 py-1 rounded-full bg-white/8 text-slate-200">{m}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <h3 className="font-heading font-bold text-navy mb-3">{title}</h3>
      {children}
    </div>
  );
}
